"use client"
import React, { useState, useEffect, forwardRef, useImperativeHandle } from "react"
import { 
  CurrencyDollarIcon, 
  ExclamationCircleIcon, 
  ClockIcon, 
  ChartBarIcon, 
  PlusIcon, 
  TrashIcon, 
  PencilIcon, 
  StarIcon 
} from "@heroicons/react/24/outline"
import { getAllGstRates } from "../../../api/courseService"
import Button from "../../../components/common/Button"

interface PaymentOptionsStepProps {
  data: any
  onChange: (data: any) => void
  onNext: (updatedData?: any) => void
  onBack: () => void
  onSaveDraft?: () => void
  loading?: boolean
  isEditMode?: boolean
}

interface GstRate {
  _id: string
  gstPercent: number
}

interface PriceDetail {
  _id?: string
  price: number
  actualPrice: number
  validityType: string
  validityInDays: number
  gstRateId: string
  _delete?: boolean
  isBestDeal?: boolean
}

export interface PaymentOptionsStepRef {
  savePaymentDetails: () => Promise<void>
  hasUnsavedChanges: () => boolean
}

const PaymentOptionsStep = forwardRef<PaymentOptionsStepRef, PaymentOptionsStepProps>(({
  data,
  onChange,
  onNext,
  onBack,
  onSaveDraft,
  loading = false,
}, ref) => {
  const [courseValidity, setCourseValidity] = useState(false)
  const [selectedPeriod, setSelectedPeriod] = useState("3 months")
  const [customDays, setCustomDays] = useState("")
  const [basePrice, setBasePrice] = useState("")
  const [actualPrice, setActualPrice] = useState("")
  const [gstAmount, setGstAmount] = useState("")
  const [gstRates, setGstRates] = useState<GstRate[]>([])
  const [selectedGstRate, setSelectedGstRate] = useState<GstRate | null>(null)
  const [loadingGstRates, setLoadingGstRates] = useState(false)
  const [priceDetails, setPriceDetails] = useState<PriceDetail[]>([])
  const [editingIndex, setEditingIndex] = useState<number | null>(null)
  // const [pricingId, setPricingId] = useState<string | null>(null)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [isBestDeal, setIsBestDeal] = useState(false)

  const periods = ["1 month", "3 months", "6 months", "1 year", "Custom (in days)"]

  const validityMap = {
    "1 month": "1month",
    "3 months": "3months",
    "6 months": "6months",
    "1 year": "1year",
    "Custom (in days)": "custom"
  }

  useEffect(() => {
    if (data.priceDetails && Array.isArray(data.priceDetails)) {
      // Convert existing price details to the new format
      const existingPriceDetails = data.priceDetails.map((detail: any, index: number) => {
        // Parse period to determine validity and period values
        let validityType = "3months"
        let validityInDays = 90
        
        if (detail.period) {
          if (detail.period.includes("days") && detail.period !== "1month" && detail.period !== "3months" && detail.period !== "6months" && detail.period !== "1year") {
            validityType = "custom"
            validityInDays = parseInt(detail.period.replace(" days", "")) || 90
          } else {
            validityType = detail.period
          }
        }
        
        return {
          _id: `temp-${index}`,
          price: detail.price || 0,
          actualPrice: detail.actualPrice || 0,
          validityType,
          validityInDays,
          gstRateId: detail.gstRateId || "",
          isBestDeal: false
        }
      })

      setPriceDetails(existingPriceDetails)
      
      if (existingPriceDetails.length > 0) {
        setCourseValidity(true)
      }
    } else {
      setPriceDetails([])
      setCourseValidity(false)
    }
    setHasUnsavedChanges(false)
  }, [data.priceDetails])

  useEffect(() => {
    const fetchGstRates = async () => {
      setLoadingGstRates(true)
      try {
        const response = await getAllGstRates()
        if (response.status === 200 && response.data) {
          setGstRates(response.data)
          const defaultGst = response.data.find((rate: GstRate) => rate.gstPercent === 12)
          if (defaultGst) {
            setSelectedGstRate(defaultGst)
          }
        }
      } catch (error: any) {
        console.error("Error fetching GST rates:", error)
      } finally {
        setLoadingGstRates(false)
      }
    }
    fetchGstRates()
  }, [])

  useEffect(() => {
    if (basePrice && selectedGstRate) {
      const gstValue = (parseFloat(basePrice) * selectedGstRate.gstPercent) / 100
      setGstAmount(gstValue.toFixed(2))
    } else {
      setGstAmount("")
    }
  }, [basePrice, selectedGstRate])

  const getDaysFromPeriod = (period: string): number | undefined => {
    switch (period) {
      case "1 month": return 30
      case "3 months": return 90
      case "6 months": return 180
      case "1 year": return 365
      case "Custom (in days)": return customDays ? Number(customDays) : undefined
      default: return undefined
    }
  }

  const totalPrice = (parseFloat(basePrice) || 0) + (parseFloat(gstAmount) || 0)

  const hasValidFormData = () => {
    // Check if required fields are filled
    if (!basePrice || !actualPrice || !selectedGstRate || !courseValidity) {
      return false;
    }
    
    // Check if prices are valid numbers and not negative
    const basePriceNum = parseFloat(basePrice);
    const actualPriceNum = parseFloat(actualPrice);
    
    if (isNaN(basePriceNum) || isNaN(actualPriceNum)) {
      return false;
    }
    
    if (basePriceNum < 0 || actualPriceNum < 0) {
      return false;
    }
    
    return true;
  };

  const calculateDiscount = (detail: PriceDetail) => {
    if (!detail.price || !detail.actualPrice) return 0
    const base = detail.price
    const actual = detail.actualPrice
    if (base <= actual) return 0
    return Math.round(((base - actual) / base) * 100)
  }

  const editPriceDetail = (index: number) => {
    const detail = priceDetails[index]
    setBasePrice(detail.price.toString())
    setActualPrice(detail.actualPrice.toString())

    const gstRate = gstRates.find(rate => rate._id === detail.gstRateId)
    if (gstRate) {
      setSelectedGstRate(gstRate)
    }

    const period = Object.entries(validityMap).find(
      ([_, value]) => value === detail.validityType
    )?.[0] || "3 months"
    setSelectedPeriod(period)

    if (detail.validityInDays && detail.validityType === "custom") {
      setCustomDays(detail.validityInDays.toString())
    }

    setIsBestDeal(detail.isBestDeal || false)
    setEditingIndex(index)
    setHasUnsavedChanges(true)
  }

  const updatePriceDetail = () => {
    if (editingIndex === null || !hasValidFormData()) {
      if (basePrice && actualPrice && (parseFloat(basePrice) < 0 || parseFloat(actualPrice) < 0)) {
        alert("Price values cannot be negative");
      } else {
        alert("Please fill all required fields with valid positive values");
      }
      return;
    }

    const validityType = validityMap[selectedPeriod as keyof typeof validityMap]
    const validityInDays = getDaysFromPeriod(selectedPeriod)

    if (!validityType || !validityInDays) {
      alert("Please select a valid validity period")
      return
    }

    const updatedPriceDetails = [...priceDetails]

    updatedPriceDetails[editingIndex] = {
      _id: updatedPriceDetails[editingIndex]._id,
      price: parseFloat(basePrice),
      actualPrice: parseFloat(actualPrice),
      validityType,
      validityInDays,
      gstRateId: selectedGstRate?._id || "",
      isBestDeal: isBestDeal
    }

    setPriceDetails(updatedPriceDetails)
    setBasePrice("")
    setActualPrice("")
    setCustomDays("")
    setSelectedPeriod("3 months")
    setIsBestDeal(false)
    setEditingIndex(null)
    setHasUnsavedChanges(false)
  }

  const addPriceDetail = () => {
    if (!hasValidFormData()) {
      if (basePrice && actualPrice && (parseFloat(basePrice) < 0 || parseFloat(actualPrice) < 0)) {
        alert("Price values cannot be negative");
      } else {
        alert("Please fill all required fields with valid positive values before adding a price option");
      }
      return
    }

    const validityType = validityMap[selectedPeriod as keyof typeof validityMap]
    const validityInDays = getDaysFromPeriod(selectedPeriod)

    if (!validityType || !validityInDays) {
      alert("Please select a valid validity period")
      return
    }

    const newPriceDetail: PriceDetail = {
      price: parseFloat(basePrice),
      actualPrice: parseFloat(actualPrice),
      validityType,
      validityInDays,
      gstRateId: selectedGstRate?._id || "",
      isBestDeal: isBestDeal
    }

    setPriceDetails([...priceDetails, newPriceDetail])
    setBasePrice("")
    setActualPrice("")
    setCustomDays("")
    setSelectedPeriod("3 months")
    setIsBestDeal(false)
    setHasUnsavedChanges(false)
  }

  const removePriceDetail = (index: number) => {
    const updatedPriceDetails = [...priceDetails]

    if (updatedPriceDetails[index]._id && updatedPriceDetails[index]._id?.startsWith('temp-')) {
      updatedPriceDetails.splice(index, 1)
    } else {
      updatedPriceDetails[index] = {
        ...updatedPriceDetails[index],
        _delete: true
      }
    }

    setPriceDetails(updatedPriceDetails)
    setHasUnsavedChanges(true)
  }

  const cancelEdit = () => {
    setBasePrice("")
    setActualPrice("")
    setCustomDays("")
    setSelectedPeriod("3 months")
    setIsBestDeal(false)
    setEditingIndex(null)
    setHasUnsavedChanges(false)
  }

  const savePaymentDetails = async () => {
    try {
      // Use the loading prop directly instead of local state

      if (hasUnsavedChanges && hasValidFormData()) {
        const shouldSave = window.confirm(
          "You have unsaved changes in the form. Do you want to add them as a new price option before continuing?"
        )

        if (shouldSave) {
          if (editingIndex !== null) {
            updatePriceDetail()
          } else {
            addPriceDetail()
          }
          await new Promise(resolve => setTimeout(resolve, 100))
        } else {
          cancelEdit()
        }
      }

      const activePriceDetails = priceDetails.filter(detail => !detail._delete)

      if (activePriceDetails.length === 0) {
        throw new Error("Please add at least one price option")
      }

      // Convert to the format expected by the parent component
      const convertedPriceDetails = activePriceDetails.map(detail => {
        let periodValue = ""
        if (detail.validityType === "custom") {
          periodValue = `${detail.validityInDays} days`
        } else {
          periodValue = detail.validityType
        }
        
        return {
          period: periodValue,
          actualPrice: detail.actualPrice,
          price: detail.price,
          gst: gstRates.find(r => r._id === detail.gstRateId)?.gstPercent || 0,
          totalprice: detail.actualPrice + (detail.actualPrice * (gstRates.find(r => r._id === detail.gstRateId)?.gstPercent || 0) / 100)
        }
      })

      const updatedData = {
        ...data,
        priceDetails: convertedPriceDetails
      }

      onChange(updatedData)
      setHasUnsavedChanges(false)
      return updatedData
    } catch (error: any) {
      console.error("Error saving payment details:", error)
      throw error
    } finally {

    }
  }

  useImperativeHandle(ref, () => ({
    savePaymentDetails,
    hasUnsavedChanges: () => hasUnsavedChanges
  }))

  const formatValidityType = (validityType: string) => {
    switch (validityType) {
      case "1month": return "1 Month"
      case "3months": return "3 Months"
      case "6months": return "6 Months"
      case "1year": return "1 Year"
      case "custom": return "Custom"
      default: return validityType
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      const updatedData = await savePaymentDetails()
      onNext(updatedData)
    } catch (error: any) {
      console.error("Error saving payment details:", error)
      alert(error.message || "Failed to save payment details")
    }
  }

  const handleSaveDraftClick = () => {
    // For draft, we don't need price details
    const updatedData = {
      ...data,
      priceDetails: []
    }
    onChange(updatedData)
    if (onSaveDraft) {
      onSaveDraft()
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-lg p-3">
        <div className="flex items-center mb-1.5">
          <div className="bg-green-100 rounded-full p-1 mr-1.5">
            <CurrencyDollarIcon className="text-green-600 w-4 h-4" />
          </div>
          <h2 className="text-lg font-bold text-gray-900">Set Your Package Pricing</h2>
        </div>
        <p className="text-gray-600 text-xs">Configure pricing, validity, and payment options to maximize your package revenue and accessibility.</p>
        {hasUnsavedChanges && (
          <div className="mt-2 p-1.5 bg-yellow-50 border border-yellow-200 rounded-md">
            <p className="text-yellow-700 text-xs flex items-center">
              <ExclamationCircleIcon className="w-3 h-3 mr-1.5" />
              You have unsaved changes. Please save or cancel before continuing.
            </p>
          </div>
        )}
      </div>

      {/* Package Validity */}
      <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-xs hover:shadow-sm transition-shadow duration-200">
        <div className="flex items-center justify-between">
          <div className="flex items-start">
            <div className="bg-orange-100 rounded-full p-1 mr-1.5 mt-0.5">
              <ClockIcon className="text-orange-600 w-3.5 h-3.5" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-1">Package Validity Period</h3>
              <p className="text-[10px] text-gray-600 max-w-md">
                Set access duration for your package. When disabled, students get lifetime access.
                Limited access can create urgency and higher engagement.
              </p>
            </div>
          </div>
          <label className="relative inline-flex items-center cursor-pointer ml-3">
            <input
              type="checkbox"
              checked={courseValidity}
              onChange={(e) => {
                setCourseValidity(e.target.checked)
                setHasUnsavedChanges(true)
              }}
              className="sr-only peer"
            />
            <div className="w-8 h-4 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:bg-orange-600"></div>
          </label>
        </div>
      </div>

      {courseValidity && (
        <>
          {/* Period Selection */}
          <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-xs">
            <div className="flex items-center mb-2">
              <div className="bg-blue-100 rounded-full p-1 mr-1.5">
                <ClockIcon className="text-blue-600 w-3.5 h-3.5" />
              </div>
              <label className="text-sm font-semibold text-gray-900">Access Duration</label>
            </div>
            <div className="relative">
              <select
                value={selectedPeriod}
                onChange={(e) => {
                  setSelectedPeriod(e.target.value)
                  setHasUnsavedChanges(true)
                }}
                className="block w-full px-2.5 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none bg-white text-gray-900 font-medium shadow-xs hover:border-gray-400 transition-colors text-xs"
              >
                {periods.map((period) => (
                  <option key={period} value={period}>
                    {period}
                  </option>
                ))}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center px-1.5 pointer-events-none">
                <svg className="w-2.5 h-2.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
          </div>

          {/* Custom Days Input */}
          {selectedPeriod === "Custom (in days)" && (
            <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-xs">
              <label className="block text-[10px] font-medium text-gray-700 mb-1">Number of Days</label>
              <input
                type="number"
                value={customDays}
                onChange={(e) => {
                  setCustomDays(e.target.value)
                  setHasUnsavedChanges(true)
                }}
                placeholder="Enter number of days"
                className="block w-full px-2.5 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none bg-white text-gray-900 font-medium shadow-xs hover:border-gray-400 transition-colors text-xs"
              />
            </div>
          )}
        </>
      )}

      {/* Pricing */}
      <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-xs">
        <div className="flex items-center mb-3">
          <div className="bg-purple-100 rounded-full p-1 mr-1.5">
            <ChartBarIcon className="text-purple-600 w-3.5 h-3.5" />
          </div>
          <h3 className="text-sm font-semibold text-gray-900">Package Pricing Details</h3>
        </div>

        <div className="flex flex-col lg:flex-row gap-4">
          {/* Left Column - Form */}
          <div className="flex-1">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="space-y-1">
                <label className="block text-[10px] font-medium text-gray-700">Base Price (₹)</label>
                <input
                  type="number"
                  value={basePrice}
                  onChange={(e) => {
                    setBasePrice(e.target.value)
                    setHasUnsavedChanges(true)
                  }}
                  placeholder="Enter base price"
                  className="block w-full px-2.5 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 appearance-none bg-white text-gray-900 font-medium shadow-xs hover:border-gray-400 transition-colors text-xs"
                />
                <p className="text-[10px] text-gray-500">Price before discounts and taxes</p>
              </div>
              <div className="space-y-1">
                <label className="block text-[10px] font-medium text-gray-700">Selling Price (₹)</label>
                <input
                  type="number"
                  value={actualPrice}
                  onChange={(e) => {
                    setActualPrice(e.target.value)
                    setHasUnsavedChanges(true)
                  }}
                  placeholder="Enter selling price"
                  className="block w-full px-2.5 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 appearance-none bg-white shadow-xs hover:border-gray-400 transition-colors text-xs"
                />
                <p className="text-[10px] text-gray-500">Final price students will pay</p>
              </div>
              <div className="space-y-1">
                <label className="block text-[10px] font-medium text-gray-700">GST Rate</label>
                <div className="relative">
                  <select
                    value={selectedGstRate?._id || ""}
                    onChange={(e) => {
                      const rate = gstRates.find(r => r._id === e.target.value)
                      setSelectedGstRate(rate || null)
                      setHasUnsavedChanges(true)
                    }}
                    className="block w-full px-2.5 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 appearance-none bg-white shadow-xs hover:border-gray-400 transition-colors text-xs"
                    disabled={loadingGstRates}
                  >
                    <option value="">Select GST Rate</option>
                    {gstRates.map((rate) => (
                      <option key={rate._id} value={rate._id}>
                        {rate.gstPercent}%
                      </option>
                    ))}
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center px-1.5 pointer-events-none">
                    <svg className="w-2.5 h-2.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
                {loadingGstRates && <p className="text-[10px] text-purple-500 mt-1 flex items-center"><div className="animate-spin rounded-full h-2 w-2 border-b-2 border-purple-500 mr-1"></div>Loading rates...</p>}
              </div>
            </div>

            <div className="mt-3 flex justify-end space-x-1.5">
              {editingIndex !== null ? (
                <>
                  <Button
                    variant="secondary"
                    onClick={cancelEdit}
                    className="flex items-center text-xs px-2.5 py-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={updatePriceDetail}
                    className="flex items-center text-xs px-2.5 py-1"
                  >
                    Update Price Option
                  </Button>
                </>
              ) : (
                <Button
                  onClick={addPriceDetail}
                  disabled={!hasValidFormData()}
                  className="flex items-center text-xs px-2.5 py-1"
                >
                  <PlusIcon className="w-3 h-3 mr-1" />
                  Add Price Option
                </Button>
              )}
            </div>
          </div>

          {/* Right Column - Preview Card */}
          {editingIndex !== null && (
            <div className="lg:w-1/3">
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-3 h-full">
                <h3 className="text-sm font-semibold text-gray-900 mb-2">Plan Preview</h3>
                <div className="bg-white rounded-md p-3 space-y-2">
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-gray-600 font-medium text-xs">Base Price:</span>
                    <span className="font-semibold text-gray-900 text-xs">₹{basePrice || "0"}</span>
                  </div>
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-gray-600 font-medium text-xs">Selling Price:</span>
                    <span className="font-semibold text-gray-900 text-xs">₹{actualPrice || "0"}</span>
                  </div>
                  <div className="flex justify-between items-center py-1.5 border-b border-gray-100">
                    <span className="text-gray-600 font-medium text-xs">GST Amount ({selectedGstRate?.gstPercent || 0}%):</span>
                    <span className="font-semibold text-gray-900 text-xs">₹{gstAmount || "0.00"}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-md px-3">
                    <span className="text-blue-900 font-bold text-sm">Total Amount:</span>
                    <span className="text-blue-900 font-bold">₹{totalPrice.toFixed(2)}</span>
                  </div>
                  {parseFloat(actualPrice) < parseFloat(basePrice) && (
                    <div className="flex items-center justify-center mt-2 p-1.5 bg-green-50 rounded-md">
                      <ExclamationCircleIcon className="text-green-600 w-3 h-3 mr-1" />
                      <span className="text-green-700 text-[10px] font-medium">
                        Students save ₹{(parseFloat(basePrice) - parseFloat(actualPrice)).toFixed(2)} with this pricing!
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Price Options List */}
      {priceDetails.filter(detail => !detail._delete).length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-xs">
          <h3 className="text-sm font-semibold text-gray-900 mb-2">Price Options</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            {priceDetails.map((detail, index) => (
              !detail._delete && (
                <div key={index} className="bg-white rounded-lg shadow-xs overflow-hidden border border-gray-200 hover:shadow-sm transition-shadow duration-200">
                  {/* Plan Header */}
                  <div className="bg-gradient-to-r from-blue-500 to-indigo-600 p-2.5 relative">
                    {detail.isBestDeal && (
                      <div className="absolute top-1.5 right-1.5 bg-green-500 text-white text-[9px] font-bold px-1 py-0.5 rounded-full flex items-center">
                        <StarIcon className="w-2 h-2 mr-0.5" />
                        Best Deal
                      </div>
                    )}
                    <h4 className="text-white font-bold text-xs">
                      {detail.validityType === "custom"
                        ? `${detail.validityInDays} Days Plan`
                        : formatValidityType(detail.validityType)}
                    </h4>
                  </div>

                  {/* Plan Content */}
                  <div className="p-2.5">
                    <div className="mb-2">
                      <div className="flex items-baseline">
                        <span className="text-base font-bold text-gray-900">₹{detail.actualPrice}</span>
                        {detail.price > detail.actualPrice && (
                          <span className="ml-1 text-[10px] text-gray-500 line-through">₹{detail.price}</span>
                        )}
                      </div>

                      {detail.price > detail.actualPrice && (
                        <div className="mt-1 text-[10px] text-green-600 font-medium">
                          Save {calculateDiscount(detail)}%
                        </div>
                      )}
                    </div>

                    <div className="space-y-1.5">
                      <div className="flex items-center text-[10px] text-gray-600">
                        <ClockIcon className="w-2.5 h-2.5 mr-1 text-blue-500" />
                        <span>
                          {detail.validityType === "custom"
                            ? `${detail.validityInDays} days access`
                            : formatValidityType(detail.validityType)}
                        </span>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="mt-2 flex justify-end space-x-1">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => editPriceDetail(index)}
                        className="text-blue-600 hover:text-blue-800 p-1"
                      >
                        <PencilIcon className="w-2.5 h-2.5" />
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => removePriceDetail(index)}
                        className="text-red-600 hover:text-red-800 p-1"
                      >
                        <TrashIcon className="w-2.5 h-2.5" />
                      </Button>
                    </div>
                  </div>
                </div>
              )
            ))}
          </div>
        </div>
      )}

      {/* Price Summary */}
      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-3">
        <div className="flex items-center mb-2">
          <div className="bg-blue-100 rounded-full p-1 mr-1.5">
            <svg className="text-blue-600 w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
          </div>
          <h3 className="text-sm font-semibold text-gray-900">Pricing Summary</h3>
        </div>
        <div className="bg-white rounded-md p-2.5 space-y-1.5">
          <div className="flex justify-between items-center py-1">
            <span className="text-gray-600 text-xs font-medium">Base Price:</span>
            <span className="font-semibold text-gray-900 text-xs">₹{basePrice || "0"}</span>
          </div>
          <div className="flex justify-between items-center py-1">
            <span className="text-gray-600 text-xs font-medium">Selling Price:</span>
            <span className="font-semibold text-gray-900 text-xs">₹{actualPrice || "0"}</span>
          </div>
          <div className="flex justify-between items-center py-1 border-b border-gray-100">
            <span className="text-gray-600 text-xs font-medium">GST Amount ({selectedGstRate?.gstPercent || 0}%):</span>
            <span className="font-semibold text-gray-900 text-xs">₹{gstAmount || "0.00"}</span>
          </div>
          <div className="flex justify-between items-center py-1.5 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-md px-2.5">
            <span className="text-blue-900 font-bold text-sm">Total Amount:</span>
            <span className="text-blue-900 font-bold">₹{totalPrice.toFixed(2)}</span>
          </div>
          {parseFloat(actualPrice) < parseFloat(basePrice) && (
            <div className="flex items-center justify-center mt-1.5 p-1 bg-green-50 rounded-md">
              <ExclamationCircleIcon className="text-green-600 w-3 h-3 mr-1" />
              <span className="text-green-700 text-[10px] font-medium">
                Students save ₹{(parseFloat(basePrice) - parseFloat(actualPrice)).toFixed(2)} with this pricing!
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Navigation Buttons */}
      <div className="flex flex-col sm:flex-row justify-between items-center pt-6 border-t border-gray-200 space-y-3 sm:space-y-0">
        <button
          type="button"
          onClick={onBack}
          className="px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:border-purple-500 hover:text-purple-600 transition-all duration-200 font-medium flex items-center space-x-1.5 text-sm"
        >
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          <span>Back</span>
        </button>
        <div className="flex space-x-2">
          {onSaveDraft && (
            <button
              type="button"
              onClick={handleSaveDraftClick}
              disabled={loading}
              className="px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:border-purple-500 hover:text-purple-600 transition-all duration-200 font-medium flex items-center space-x-1.5 disabled:opacity-50 text-sm"
            >
              <span>Save as Draft</span>
            </button>
          )}
          <button
            type="submit"
            className="px-6 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 transform hover:scale-105 transition-all duration-200 shadow-md hover:shadow-lg font-semibold flex items-center space-x-1.5 text-sm"
          >
            <span>Continue</span>
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>
    </form>
  )
})

export default PaymentOptionsStep